import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { formatDateATTime } from "../../../../utils/commonfunction";
import { DYNAMIC_VARIABLES } from "../../../../utils/Constants";
import { getPurchaseHistory } from "../../services/packages.service";
import { MousePointer2, Clock } from "lucide-react";

function PackagePurchaseHistory() {
  const { t } = useTranslation();
  const location = useLocation();
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [dateFilter, setDateFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [activeTab, setActiveTab] = useState(location.state?.activeTab || "Hours");

  const fetchPurchaseHistory = async () => {
    try {
      const res = await getPurchaseHistory({ type: activeTab });
      if (res?.success) {
        setData(res.data);
        setFilteredData(res.data);
      }
    } catch (error) {
      console.log("CMS Fetch Error:", error);
    }
  };

  useEffect(() => {
    fetchPurchaseHistory();
  }, [activeTab]);

  // Apply Filters
  useEffect(() => {
    let temp = [...data];

    // Date Filter
    if (dateFilter) {
      temp = temp.filter((item) => {
        if (!item?.purchase_time) return false;
        try {
          const purchaseDate = new Date(item.purchase_time)
            .toISOString()
            .split("T")[0];
          return purchaseDate === dateFilter;
        } catch (e) {
          return false;
        }
      });
    }

    // Status Filter
    if (statusFilter) {
      temp = temp.filter(
        (item) => item.paymentDetails?.payment_status === statusFilter
      );
    }

    // Type Filter
    temp = temp.filter((item) => {
      const type = item.packageDetails?.Type || "Hours";
      return type === activeTab;
    });

    setFilteredData(temp);
  }, [dateFilter, statusFilter, activeTab, data]);

  return (
    <div className="dashbaord-bg py-10">
      <div className="container">
        {/* Header */}
        <div className="border-b border-gray-200 pb-[15px] mb-[30px] flex items-center justify-between flex-wrap gap-[15px]">
          <h1 className="headings-h1 text-zinc-950 font-bold text-3xl">
            {t('advertiser.purchase_history.heading')}
          </h1>
        </div>

        {/* Tabs Section */}
        <div className="flex gap-8 border-b border-gray-200 mb-8">
          <button
            onClick={() => setActiveTab("Hours")}
            className={`pb-2 px-1 text-lg font-semibold transition-colors duration-200 ${activeTab === "Hours"
              ? "border-b-2 border-primary-400 text-primary-400"
              : "text-zinc-500 hover:text-zinc-700"
              }`}
          >
            {t('advertiser.packages.hour_packages')}
          </button>
          <button
            onClick={() => setActiveTab("AiClicks")}
            className={`pb-2 px-1 text-lg font-semibold transition-colors duration-200 ${activeTab === "AiClicks"
              ? "border-b-2 border-[#5f63b8] text-[#5f63b8]"
              : "text-zinc-500 hover:text-zinc-700"
              }`}
          >
            {t('advertiser.packages.ai_packages')}
          </button>
        </div>

        {/* Filter Bar */}
        <div className="flex flex-wrap gap-3 mb-6 items-center">
          {/* Date Filter */}
          <input
            type="date"
            className="form-control-custom max-w-[248px]"
            value={dateFilter}
            onChange={(e) => setDateFilter(e.target.value)}
          />

          {/* Status Filter */}
          <select
            className="form-control-custom max-w-[248px]"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="">{t('advertiser.purchase_history.select_status')}</option>
            <option value="completed">{t('advertiser.purchase_history.successful')}</option>
            <option value="refunded">{t('advertiser.purchase_history.refunded')}</option>
          </select>
        </div>

        {/* Purchase History */}
        {filteredData?.length > 0 ? (
          <div className="flex flex-col gap-4">
            {filteredData.map((item, index) => (
              <div
                key={index}
                className="bg-white p-[20px] md:p-[25px] rounded-[12px] shadow-[0px_2px_24px_rgba(0,0,0,0.04)] border !border-zinc-100 transition-all duration-300 hover:shadow-lg"
              >
                <div className="flex justify-between items-start gap-4 flex-wrap">
                  {/* Left Section: Details */}
                  <div className="flex-1 min-w-[200px]">
                    <div className="flex items-center gap-3 mb-2">
                      <span className="px-2 py-1 bg-zinc-50 border border-zinc-100 rounded text-[11px] font-bold text-zinc-400 uppercase tracking-wider">
                        #{item.purchase_id}
                      </span>
                      <span className="body-sm text-zinc-400">
                        {formatDateATTime(item.purchase_time)}
                      </span>
                    </div>

                    <h3 className="headings-h4 text-zinc-900 mb-2">
                      {item.packageDetails?.Name || t('common.unnamed_package')}
                    </h3>

                    <div className="flex items-center gap-4">
                      <div className="flex items-center gap-2">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                          item.packageDetails?.Type === "AiClicks" ? "bg-indigo-50" : "bg-orange-50"
                        }`}>
                          {item.packageDetails?.Type === "AiClicks" ? (
                            <MousePointer2 size={16} className="text-indigo-500" />
                          ) : (
                            <Clock size={16} className="text-orange-500" />
                          )}
                        </div>
                        <div>
                          <p className="text-[10px] uppercase font-bold text-zinc-400 leading-none mb-1">
                            {item.packageDetails?.Type === "AiClicks" ? t('advertiser.purchase_history.num_clicks') : t('advertiser.purchase_history.num_hours')}
                          </p>
                          <p className="body-md font-bold text-zinc-700 leading-none">
                            {item.packageDetails?.Type === "AiClicks" ? (item.packageDetails?.AiClicks ?? 0) : (item.packageDetails?.Hours ?? 0)}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Right Section: Status and Price */}
                  <div className="flex flex-col items-end gap-3 text-right">
                    <div className={`px-3 py-1 rounded-full text-[12px] font-bold tracking-wide ${
                      item.paymentDetails?.payment_status === "completed"
                        ? "bg-emerald-50 text-emerald-600 border border-emerald-100"
                        : "bg-red-50 text-red-600 border border-red-100"
                    }`}>
                      {item.paymentDetails?.payment_status === "completed"
                        ? t('advertiser.purchase_history.successful').toUpperCase()
                        : t('advertiser.purchase_history.refunded').toUpperCase()}
                    </div>

                    <p className="headings-h2 text-zinc-900 leading-none">
                      <span className="text-lg font-medium mr-1">{t('common.currency')}</span>
                      {item.packageDetails?.Price || 0}
                    </p>

                    {item.invoice_pdf || item.invoice_url ? (
                      <button
                        onClick={() => {
                          const targetUrl = item.invoice_url || 
                            (item.invoice_pdf?.startsWith("http") 
                              ? item.invoice_pdf 
                              : `${DYNAMIC_VARIABLES.SERVER_URL}/uploads/invoices/${item.invoice_pdf}`);
                          window.open(targetUrl, '_blank');
                        }}
                        className="flex items-center gap-2 px-3 py-2 bg-white border border-zinc-200 rounded-lg text-[13px] font-semibold text-zinc-600 transition-all hover:bg-zinc-50 hover:border-zinc-300 hover:text-zinc-900 group"
                      >
                        <svg 
                          width="16" 
                          height="16" 
                          viewBox="0 0 24 24" 
                          fill="none" 
                          stroke="currentColor" 
                          strokeWidth="2" 
                          strokeLinecap="round" 
                          strokeLinejoin="round"
                          className="transition-transform group-hover:translate-y-[1px]"
                        >
                          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                          <polyline points="7 10 12 15 17 10" />
                          <line x1="12" y1="15" x2="12" y2="3" />
                        </svg>
                        {t('advertiser.purchase_history.download_invoice')}
                      </button>
                    ) : (
                      <p className="text-[12px] text-zinc-500 italic mt-2 text-right max-w-[200px]">
                        Invoice will be generated at the end of month for purchased packages
                      </p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="bg-white p-[30px] rounded-[10px] shadow-[0px_2px_24px_0px_rgba(0,0,0,0.05)] border !border-zinc-100 text-center">
            <p className="body-lg text-zinc-500 italic">{t('advertiser.purchase_history.no_records')}</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default PackagePurchaseHistory;